import * as React from "react";
import { observer } from "mobx-react";
import { UitkImage, UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { UitkCard, UitkCardMedia, UitkCardLink } from "uitk-react-cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { DestinationSuggestionsProps } from "../typings";
import { Destination } from "src/typings/microserviceModels/destination-suggestions-flex-module.d.ts";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { useLocalization } from "@shared-ui/localization-context";
import { withMabTracker, MabTrackEvent } from "components/utility/analytics/MabTracker";
import {
  PageBackgroundContext,
  BackgroundColorTokens,
} from "src/components/flexComponents/PageLevelProviders/PageBackgroundColor/PageBackgroundContext.tsx";

const EmbeddingCards = withMabTracker(
  ({
    destination,
    cardIndex,
    mabTrack,
  }: {
    destination: Destination;
    cardIndex: number;
    mabTrack?: (x: MabTrackEvent) => null;
  }) => {
    const rfrrId = cardIndex + 1;

    const cardClicked = mabTrack
      ? () => {
          const mabTrackData: MabTrackEvent = {
            moduleName: "embedding",
            eventTarget: "embedding",
            eventType: "homepageClick.nonPath",
            rfrr: rfrrId.toString(),
          };
          mabTrack(mabTrackData);
        }
      : undefined;

    const backgroundColor = React.useContext(PageBackgroundContext);
    const withBorder = backgroundColor === BackgroundColorTokens.BACKGROUND_WHITE;

    return (
      <>
        <UitkCard
          className="FullBleedCard uitk-flex-justify-content-flex-start uitk-flex-column"
          withBorder={withBorder}
        >
          <UitkCardMedia>
            <UitkFigure ratio={UitkFigureAspectRatioType.R3_2}>
              <UitkImage src={destination.destinationImg} alt={destination.imgAlt} />
              <UitkScrim type={UitkScrimType.BOTTOM}>
                <UitkLayoutFlex justifyContent="space-between">
                  <>
                    <UitkLayoutFlexItem>
                      <div>
                        <h3 className="uitk-type-heading-500 cardContent">{destination.name}</h3>
                      </div>
                    </UitkLayoutFlexItem>
                  </>
                </UitkLayoutFlex>
              </UitkScrim>
            </UitkFigure>
            <UitkCardLink>
              <TrackedLink
                moduleName="Embedding"
                action={Action.CLICK}
                href={destination.url}
                rfrr={rfrrId}
                onClick={cardClicked}
              >
                {destination.name}
              </TrackedLink>
            </UitkCardLink>
          </UitkCardMedia>
        </UitkCard>
      </>
    );
  }
);

export const Embedding = observer(({ props }: { props: DestinationSuggestionsProps }) => {
  const keyHelper = new ItemKeyHelper(props.flexComponent.id);
  const { formatText } = useLocalization();
  const title = formatText("destinationSuggestions.embedding.title");

  return (
    <div
      data-testid="embedding"
      className="DestinationSuggestions uitk-cell all-b-margin-three"
      id={props.flexComponent.id}
      data-fm={props.flexComponent.fmId}
    >
      <div className="s-l-padding-three m-l-padding-three">
        <div className="title-wrapper all-t-padding-three">
          <h2 className="title uitk-type-display-700">{title}</h2>
        </div>
        <div className="subtitle-wrapper all-b-margin-three" />
      </div>
      <IsomorphicCarousel
        className="carousel-component"
        itemsVisible={{
          lg: 3,
          md: 2,
          sm: 1,
        }}
        buttonText={{
          nextButton: formatText("carousel.item.next"),
          prevButton: formatText("carousel.item.prev"),
        }}
        pageBy={1}
        peekWidth={22.8}
      >
        {props.flexComponent.model.destinations.map((destination, index) => {
          return (
            <div className="uitk-carousel-item uitk-cell" key={keyHelper.next()}>
              <EmbeddingCards destination={destination} cardIndex={index} mabTrack={props.mabTrack} />
            </div>
          );
        })}
      </IsomorphicCarousel>
    </div>
  );
});
(Embedding as React.SFC).displayName = "EmbeddingView";
